import { dateMs } from './calc';
import type { DateValue, GroupBy, OptionColor, PropertyDef, RowData } from './db-types';
import type { ValueOf } from './filter';
import { locale, t as tr } from '@lumen/platform';

export interface RowGroup {
  /** Stable key ('' = the "No <property>" group). */
  key: string;
  label: string;
  color?: OptionColor;
  rows: RowData[];
}

const DAY = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');
const isoDay = (t: number) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const RELATIVE: { key: string; label: string; test: (days: number) => boolean }[] = [
  { key: 'today', label: tr('Today'), test: (d) => d === 0 },
  { key: 'yesterday', label: tr('Yesterday'), test: (d) => d === -1 },
  { key: 'tomorrow', label: tr('Tomorrow'), test: (d) => d === 1 },
  { key: 'past7', label: tr('Previous 7 days'), test: (d) => d < -1 && d >= -7 },
  { key: 'next7', label: tr('Next 7 days'), test: (d) => d > 1 && d <= 7 },
  { key: 'past30', label: tr('Previous 30 days'), test: (d) => d < -7 && d >= -30 },
  { key: 'next30', label: tr('Next 30 days'), test: (d) => d > 7 && d <= 30 },
  { key: 'older', label: tr('Older'), test: (d) => d < -30 },
  { key: 'later', label: tr('Later'), test: (d) => d > 30 },
];

/** Group key and label for a date, by granularity. */
export function dateGroup(t: number, by: NonNullable<GroupBy['dateBy']>, now: Date): { key: string; label: string; sort: number } {
  const d = new Date(t);
  if (by === 'relative') {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const days = Math.floor((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - today) / DAY);
    const i = RELATIVE.findIndex((r) => r.test(days));
    const r = RELATIVE[i]!;
    const order = ['older', 'past30', 'past7', 'yesterday', 'today', 'tomorrow', 'next7', 'next30', 'later'];
    return { key: r.key, label: r.label, sort: order.indexOf(r.key) };
  }
  if (by === 'day') return { key: isoDay(t), label: d.toLocaleDateString(locale() ?? 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }), sort: new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() };
  if (by === 'week') {
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
    return { key: `w${isoDay(start.getTime())}`, label: tr('Week of {date}', { date: start.toLocaleDateString(locale() ?? 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }), sort: start.getTime() };
  }
  if (by === 'month') return { key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, sort: d.getFullYear() * 12 + d.getMonth() };
  return { key: String(d.getFullYear()), label: String(d.getFullYear()), sort: d.getFullYear() };
}

export interface GroupOptions {
  memberName?: (id: string) => string;
  rowTitle?: (id: string) => string;
  now?: Date;
}

/**
 * Groups rows by a property. Select/status groups follow option order, multi-select and person
 * rows appear in every group they belong to, dates group by relative/day/week/month/year. Group
 * order: manual (dragged), alphabetical or reverse. Hidden groups are dropped; "Hide empty groups"
 * drops groups without rows. The "No <property>" group (key '') comes last.
 */
export function groupRows(rows: RowData[], groupBy: GroupBy, prop: PropertyDef, valueOf: ValueOf, opts: GroupOptions = {}): RowGroup[] {
  const now = opts.now ?? new Date();
  const name = opts.memberName ?? ((id: string) => id);
  const groups = new Map<string, RowGroup & { sort: number | string }>();
  const ensure = (key: string, label: string, sort: number | string, color?: OptionColor) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, label, rows: [], sort, ...(color ? { color } : {}) };
      groups.set(key, g);
    }
    return g;
  };
  const noneLabel = `No ${prop.name}`;
  if (prop.type === 'select' || prop.type === 'status' || prop.type === 'multi_select') {
    (prop.options ?? []).forEach((o, i) => ensure(o.id, o.name, i, o.color));
    // The "No <property>" group is always there for option properties (use "Hide empty groups" to drop it).
    ensure('', noneLabel, Number.MAX_SAFE_INTEGER);
  }
  if (prop.type === 'checkbox') {
    ensure('true', 'Checked', 0);
    ensure('false', 'Unchecked', 1);
  }
  for (const row of rows) {
    const v = valueOf(row, prop);
    const add = (key: string, label: string, sort: number | string, color?: OptionColor) => ensure(key, label, sort, color).rows.push(row);
    switch (prop.type) {
      case 'select':
      case 'status':
        if (v) add(v as string, prop.options?.find((o) => o.id === v)?.name ?? '', prop.options?.findIndex((o) => o.id === v) ?? 0);
        else add('', noneLabel, Number.MAX_SAFE_INTEGER);
        break;
      case 'multi_select':
      case 'person':
      case 'relation': {
        const ids = v as string[];
        if (!ids.length) add('', noneLabel, Number.MAX_SAFE_INTEGER);
        for (const id of ids) {
          const label = prop.type === 'multi_select' ? (prop.options?.find((o) => o.id === id)?.name ?? '') : prop.type === 'person' ? name(id) : (opts.rowTitle?.(id) ?? id);
          add(id, label, prop.type === 'multi_select' ? (prop.options?.findIndex((o) => o.id === id) ?? 0) : label);
        }
        break;
      }
      case 'checkbox':
        add(v ? 'true' : 'false', v ? 'Checked' : 'Unchecked', v ? 0 : 1);
        break;
      case 'created_by':
      case 'last_edited_by':
        add(v as string, name(v as string), name(v as string));
        break;
      case 'date':
      case 'created_time':
      case 'last_edited_time': {
        const t = typeof v === 'number' ? v : v ? dateMs(v as DateValue) : null;
        if (t === null) add('', noneLabel, Number.MAX_SAFE_INTEGER);
        else {
          const g = dateGroup(t, groupBy.dateBy ?? 'relative', now);
          add(g.key, g.label, g.sort);
        }
        break;
      }
      default: {
        const s = v === null || v === undefined || v === '' ? '' : String(v);
        if (!s) add('', noneLabel, Number.MAX_SAFE_INTEGER);
        else add(s, s, typeof v === 'number' ? v : s.toLowerCase());
      }
    }
  }
  let list = [...groups.values()];
  const cmp = (a: { sort: number | string; key: string }, b: { sort: number | string; key: string }) => {
    if (a.key === '' || b.key === '') return a.key === '' ? 1 : b.key === '' ? -1 : 0;
    return typeof a.sort === 'number' && typeof b.sort === 'number' ? a.sort - b.sort : String(a.sort).localeCompare(String(b.sort), 'en', { numeric: true });
  };
  if (groupBy.order === 'manual' && groupBy.manual?.length) {
    const pos = (k: string) => {
      const i = groupBy.manual!.indexOf(k);
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    list.sort((a, b) => pos(a.key) - pos(b.key) || cmp(a, b));
  } else if (groupBy.order === 'asc' || groupBy.order === 'desc') {
    list.sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : a.label.localeCompare(b.label, 'en', { numeric: true })));
    if (groupBy.order === 'desc') {
      const none = list.filter((g) => g.key === '');
      list = [...list.filter((g) => g.key !== '').reverse(), ...none];
    }
  } else list.sort(cmp);
  if (groupBy.hidden?.length) list = list.filter((g) => !groupBy.hidden!.includes(g.key));
  if (groupBy.hideEmpty) list = list.filter((g) => g.rows.length > 0);
  return list.map(({ sort: _sort, ...g }) => g);
}

/** Moves group `key` before `beforeKey` (or to the end) in a manual group order. */
export function moveGroup(order: string[], allKeys: string[], key: string, beforeKey: string | null): string[] {
  const base = [...order.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !order.includes(k))].filter((k) => k !== key);
  const i = beforeKey === null ? base.length : base.indexOf(beforeKey);
  base.splice(i < 0 ? base.length : i, 0, key);
  return base;
}
